<template>
  <aside class="sidebar">
    <div class="sidebar-head">
      <div>
        <div class="eyebrow">Panel</div>
        <h2>Mis hojas</h2>
      </div>
      <button class="close-btn" @click="$emit('close')">×</button>
    </div>

    <div class="sidebar-actions">
      <button class="create-btn" @click="$emit('create-list', {})">
        <span>＋</span>
        <span>Nueva hoja</span>
      </button>
      <button class="account-btn" @click="$emit('open-account')">Mi cuenta</button>
    </div>

    <div class="section">
      <div class="section-title">Hoja rápida</div>
      <button
        v-if="quickList"
        class="list-card compact"
        :class="{ active: selectedListId === quickList.id }"
        @click="$emit('select-list', quickList.id)"
      >
        <span class="color-dot" :style="{ backgroundColor: resolveColor(quickList.color) }"></span>
        <span class="list-copy">
          <span class="list-title">{{ quickList.title }}</span>
          <span class="list-subtitle">Siempre al inicio</span>
        </span>
      </button>
    </div>

    <div class="separator"></div>

    <div
      class="section drop-zone"
      :class="{ 'drop-active': dragTarget.type === 'personal' }"
      @dragenter.prevent="handleDragEnter('personal')"
      @dragover.prevent="handleDragEnter('personal')"
      @dragleave.prevent="handleDragLeave('personal')"
      @drop.prevent="handleDropPersonal"
    >
      <div v-if="dragTarget.type === 'personal'" class="drop-overlay">Mover hoja a notas personales</div>
      <div class="section-title">Notas personales</div>

      <button
        v-for="list in personalLists"
        :key="list.id"
        class="list-card compact"
        :class="{ active: selectedListId === list.id, draggable: canMoveList(list) }"
        :draggable="canMoveList(list)"
        @dragstart="handleDragStart(list, $event)"
        @dragend="handleDragEnd"
        @click="$emit('select-list', list.id)"
      >
        <span class="color-dot" :style="{ backgroundColor: resolveColor(list.color) }"></span>
        <span class="list-copy">
          <span class="list-title">{{ list.title }}</span>
          <span class="list-subtitle">Personal</span>
        </span>
      </button>

      <button class="section-create" @click="$emit('create-list', { scopeType: 'personal' })">+ Nueva nota personal</button>
    </div>

    <div class="section">
      <div class="section-row">
        <div class="section-title">Grupos</div>
        <button class="soft-btn" @click="$emit('open-groups')">{{ mode === 'guest' ? 'Activar' : 'Gestionar' }}</button>
      </div>

      <template v-if="groups.length">
        <div
          v-for="group in groups"
          :key="group.id"
          class="group-box drop-zone"
          :class="{ 'drop-active': dragTarget.type === 'group' && dragTarget.groupId === group.id }"
          @dragenter.prevent="handleDragEnter('group', group.id)"
          @dragover.prevent="handleDragEnter('group', group.id)"
          @dragleave.prevent="handleDragLeave('group', group.id)"
          @drop.prevent="handleDropGroup(group)"
        >
          <div v-if="dragTarget.type === 'group' && dragTarget.groupId === group.id" class="drop-overlay">
            Mover hoja a {{ group.name }}
          </div>

          <div class="group-head">
            <div class="group-main">
              <span class="group-mark" :style="{ backgroundColor: group.color || '#AFCBFF' }"></span>
              <div>
                <div class="group-name">{{ group.name }}</div>
                <div class="group-meta">Notas compartidas</div>
              </div>
            </div>
          </div>

          <div class="group-lists">
            <button
              v-for="list in groupLists[group.id] || []"
              :key="list.id"
              class="list-card nested"
              :class="{ active: selectedListId === list.id, draggable: canMoveList(list) }"
              :draggable="canMoveList(list)"
              @dragstart="handleDragStart(list, $event)"
              @dragend="handleDragEnd"
              @click="$emit('select-list', list.id)"
            >
              <span class="color-dot" :style="{ backgroundColor: resolveColor(list.color) }"></span>
              <span class="list-copy">
                <span class="list-title">{{ list.title }}</span>
                <span class="list-subtitle">{{ group.name }}</span>
              </span>
            </button>
            <button class="section-create nested-create" @click="$emit('create-list', { groupId: group.id, scopeType: 'group' })">+ Nueva nota en {{ group.name }}</button>
          </div>
        </div>
      </template>

      <div v-else class="group-empty">
        {{ mode === 'guest' ? 'Inicia sesión para crear grupos compartidos.' : 'Todavía no tienes grupos.' }}
      </div>
    </div>

    <div v-if="!noAdsActive" class="section billing-wrap">
      <BillingPanel
        :mode="mode"
        :billing="billing"
        @open-auth="$emit('open-auth')"
        @upgrade="$emit('upgrade')"
        @manage="$emit('manage-billing')"
      />
    </div>
  </aside>
</template>

<script>
import { normalizeNoteColor } from '../constants/note-colors';
import BillingPanel from './BillingPanel.vue';

function sortSidebarLists(lists = []) {
  return [...lists].sort((a, b) => {
    const quickDiff = Number(b?.is_quick_sheet || 0) - Number(a?.is_quick_sheet || 0);
    if (quickDiff !== 0) return quickDiff;
    const groupA = String(a?.group_id || '');
    const groupB = String(b?.group_id || '');
    if (groupA !== groupB) return groupA.localeCompare(groupB);
    const orderDiff = Number(a?.sort_order || 0) - Number(b?.sort_order || 0);
    if (orderDiff !== 0) return orderDiff;
    return String(a?.created_at || '').localeCompare(String(b?.created_at || ''));
  });
}

export default {
  name: 'NoteSidebar',
  components: { BillingPanel },
  props: {
    lists: { type: Array, default: () => [] },
    groups: { type: Array, default: () => [] },
    selectedListId: { type: String, default: null },
    mode: { type: String, default: 'guest' },
    billing: { type: Object, default: () => ({}) },
    currentUserId: { type: Number, default: 0 },
  },
  emits: ['select-list', 'create-list', 'open-groups', 'close', 'open-auth', 'upgrade', 'manage-billing', 'open-account', 'request-move-list'],
  data() {
    return {
      draggingList: null,
      dragTarget: { type: null, groupId: null },
      dragDepth: 0,
    };
  },
  computed: {
    quickList() {
      const ordered = sortSidebarLists(this.lists);
      if (this.mode === 'user') {
        return ordered.find((item) => !item.deleted_at && Number(item.is_quick_sheet || 0) === 1) || null;
      }
      return ordered.find((item) => item.id === 'guest-inbox' && !item.deleted_at) || null;
    },
    personalLists() {
      return sortSidebarLists(this.lists).filter((item) => {
        if (item.deleted_at || item.group_id) return false;
        if (this.mode === 'user') {
          return Number(item.is_quick_sheet || 0) !== 1 && item.id !== 'guest-inbox';
        }
        return item.id !== 'guest-inbox';
      });
    },
    groupLists() {
      return this.groups.reduce((acc, group) => {
        acc[group.id] = sortSidebarLists(this.lists).filter((item) => item.group_id === group.id);
        return acc;
      }, {});
    },
    noAdsActive() {
      if (this.mode === 'user' && !this.billing?.loaded) return true;
      const plan = String(this.billing?.plan || '').toLowerCase();
      const status = String(this.billing?.status || '').toLowerCase();
      return !!(
        this.billing?.ad_free_active
        || ['monthly', 'yearly', 'lifetime'].includes(plan)
        || ['active', 'trialing', 'processing', 'complete', 'paid'].includes(status)
      );
    },
  },
  methods: {
    resolveColor(color) {
      return normalizeNoteColor(color);
    },
    canMoveList(list) {
      if (!list || this.mode !== 'user') return false;
      if (Number(list.is_quick_sheet || 0) === 1 || list.id === 'guest-inbox') return false;
      if (!list.group_id) return true;
      return Number(list.author_user_id || 0) === Number(this.currentUserId || 0);
    },
    handleDragStart(list, event) {
      if (!this.canMoveList(list)) {
        event.preventDefault();
        return;
      }
      this.draggingList = list;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', list.id);
    },
    handleDragEnd() {
      this.draggingList = null;
      this.dragTarget = { type: null, groupId: null };
      this.dragDepth = 0;
    },
    handleDragEnter(type, groupId = null) {
      if (!this.draggingList) return;
      this.dragDepth += 1;
      this.dragTarget = { type, groupId };
    },
    handleDragLeave(type, groupId = null) {
      if (!this.draggingList) return;
      if (this.dragTarget.type === type && this.dragTarget.groupId === groupId) {
        this.dragDepth = Math.max(0, this.dragDepth - 1);
        if (this.dragDepth === 0) {
          this.dragTarget = { type: null, groupId: null };
        }
      }
    },
    handleDropPersonal() {
      if (!this.draggingList) return;
      if (!this.draggingList.group_id) {
        this.handleDragEnd();
        return;
      }
      this.$emit('request-move-list', {
        list: this.draggingList,
        targetScopeType: 'personal',
        targetGroupId: null,
      });
      this.handleDragEnd();
    },
    handleDropGroup(group) {
      if (!this.draggingList) return;
      if (this.draggingList.group_id === group.id) {
        this.handleDragEnd();
        return;
      }
      this.$emit('request-move-list', {
        list: this.draggingList,
        targetScopeType: 'group',
        targetGroupId: group.id,
        targetGroupName: group.name,
      });
      this.handleDragEnd();
    },
  },
};
</script>

<style scoped>
.sidebar {
  height: 100%;
  max-height: 100%;
  overflow: auto;
  overscroll-behavior: contain;
  padding: 18px;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.24);
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.14);
  backdrop-filter: blur(18px);
  display: grid;
  align-content: start;
}
.sidebar-head,
.section-row,
.group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.eyebrow,
.section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #64748b;
  font-weight: 700;
}
h2 {
  margin: 6px 0 0;
  font-size: 28px;
  color: #0f172a;
}
.sidebar-actions {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  margin-top: 16px;
}
.close-btn,
.account-btn,
.create-btn,
.soft-btn,
.section-create {
  border: none;
  cursor: pointer;
}
.close-btn {
  display: none;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  aspect-ratio: 1 / 1;
  padding: 0;
  box-sizing: border-box;
  flex: 0 0 40px;
  border-radius: 12px;
  background: rgba(241, 245, 249, 0.96);
  color: #334155;
  font-size: 24px;
  line-height: 1;
}
.create-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 18px;
  padding: 14px 16px;
  background: #111827;
  color: white;
  font-weight: 700;
}
.account-btn,
.soft-btn {
  border-radius: 16px;
  padding: 12px 14px;
  background: rgba(59,130,246,.1);
  color: #1d4ed8;
  font-weight: 700;
}
.section {
  margin-top: 18px;
  display: grid;
  gap: 10px;
}
.separator {
  height: 1px;
  margin-top: 18px;
  background: linear-gradient(90deg, rgba(148,163,184,.12), rgba(148,163,184,.42), rgba(148,163,184,.12));
}
.list-card {
  width: 100%;
  display: grid;
  grid-template-columns: 12px 1fr;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  background: rgba(248, 250, 252, 0.92);
  cursor: pointer;
  text-align: left;
  transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease;
}
.list-card.compact {
  padding: 10px 12px;
  border-radius: 16px;
}
.list-card.nested {
  padding: 9px 12px;
  border-radius: 14px;
}
.list-card:hover,
.list-card.active {
  transform: translateY(-1px);
  border-color: rgba(59,130,246,.28);
  box-shadow: 0 10px 26px rgba(15,23,42,.08);
}
.list-card.draggable {
  cursor: grab;
}
.list-card.draggable:active {
  cursor: grabbing;
}
.list-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}
.list-title {
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.list-subtitle,
.group-meta {
  font-size: 12px;
  color: #64748b;
}
.color-dot,
.group-mark {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,.95);
  box-shadow: 0 0 0 1px rgba(148,163,184,.24);
}
.group-box {
  position: relative;
  padding: 12px;
  border-radius: 18px;
  background: rgba(248,250,252,.86);
  border: 1px solid rgba(148,163,184,.18);
}
.group-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
.group-name {
  font-weight: 800;
  color: #0f172a;
}
.group-lists {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}
.section-create {
  border-radius: 14px;
  padding: 11px 12px;
  background: rgba(15,23,42,.05);
  color: #334155;
  font-weight: 700;
}
.nested-create {
  margin-top: 4px;
}
.group-empty {
  padding: 14px;
  border-radius: 16px;
  background: rgba(248,250,252,.74);
  border: 1px dashed rgba(148,163,184,.28);
  color: #64748b;
  font-size: 14px;
  line-height: 1.45;
}
.drop-zone {
  position: relative;
}
.drop-zone.drop-active {
  border-radius: 18px;
  box-shadow: inset 0 0 0 2px rgba(59,130,246,.22);
}
.drop-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
  padding: 18px;
  border-radius: 18px;
  background: rgba(239,246,255,.94);
  color: #1d4ed8;
  font-size: 16px;
  font-weight: 900;
  z-index: 3;
  pointer-events: none;
}
.billing-wrap {
  margin-top: 24px;
}
@media (max-width: 980px) {
  .close-btn {
    display: inline-grid;
    place-items: center;
  }
  .sidebar-actions {
    grid-template-columns: 1fr;
  }
}
</style>
